import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import jwt_decode from "jwt-decode";
import { clearStandaloneUser, markStandaloneUser } from "../../utils/standaloneUser";
import { SESSION_STORAGE_BLOCKED_MESSAGE, setSessionToken } from "../../lib/sessionToken";

export default function StandaloneCallback({ setAuth }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const completeStandaloneLogin = async () => {
      const authError = searchParams.get("error");
      const errorDescription = searchParams.get("error_description");

      if (authError) {
        clearStandaloneUser();
        setError(errorDescription || authError);
        return;
      }

      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (!code || !state) {
        clearStandaloneUser();
        setError("Missing authorization code.");
        return;
      }

      const storedState = localStorage.getItem("standalone_oidc_state");
      const codeVerifier = localStorage.getItem("standalone_oidc_code_verifier");
      const nonce = localStorage.getItem("standalone_oidc_nonce");

      if (!storedState || state !== storedState) {
        clearStandaloneUser();
        setError("Invalid authentication state.");
        return;
      }

      if (!codeVerifier) {
        clearStandaloneUser();
        setError("Missing PKCE verifier.");
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/auth/standalone`;
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/standalone/token`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              code,
              code_verifier: codeVerifier,
              redirect_uri: redirectUri,
              nonce,
            }),
          },
        );

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 403) {
            clearStandaloneUser();
            navigate("/auth/standalone/unauthorized", { replace: true });
            return;
          }
          clearStandaloneUser();
          setError(data?.message || data?.error || "Standalone sign-in failed.");
          return;
        }

        if (!data?.token || !data?.userName) {
          clearStandaloneUser();
          setError("Invalid standalone login response.");
          return;
        }

        if (!setSessionToken(data.token)) {
          clearStandaloneUser();
          setError(SESSION_STORAGE_BLOCKED_MESSAGE);
          return;
        }
        localStorage.setItem("userName", data.userName);
        if (data.pylonEmailHash) {
          localStorage.setItem("pylonEmailHash", data.pylonEmailHash);
        } else {
          localStorage.removeItem("pylonEmailHash");
        }
        localStorage.removeItem("standalone_oidc_state");
        localStorage.removeItem("standalone_oidc_nonce");
        localStorage.removeItem("standalone_oidc_code_verifier");
        markStandaloneUser(true);

        const decoded_token = jwt_decode(data.token);
        const user_email = (
          decoded_token?.user?.email ||
          decoded_token?.email ||
          ""
        )
          .trim()
          .toLowerCase();
        if (user_email) {
          localStorage.setItem("userEmail", user_email);
        }

        window.pylon = {
          chat_settings: {
            app_id: "6000e45a-6b53-4401-93b4-a8d408e9b062",
            ...(user_email ? { email: user_email } : {}),
            name: data.userName || "",
            ...(data.pylonEmailHash ? { email_hash: data.pylonEmailHash } : {}),
          },
        };

        setAuth(true);
        navigate("/dashboard", { replace: true });
      } catch (err) {
        console.error("Standalone callback failed:", err);
        clearStandaloneUser();
        setError("Unable to complete standalone sign-in.");
      }
    };

    completeStandaloneLogin();
  }, [searchParams, navigate, setAuth]);

  if (error) {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        className="w-[100dvw] min-h-screen flex items-center justify-center bg-background1 px-4 py-12 focus:outline-none"
      >
        <Helmet><title>Authentication Error – Knowtex</title></Helmet>
        <div className="w-full max-w-lg bg-white rounded-xl shadow-knowtex-shadow p-10 text-center">
          <h1 className="text-2xl text-accent1 font-semibold mb-4">Sign-in Error</h1>
          <p role="alert" className="text-knowtex-grey mb-6">
            {error}
          </p>
          <button
            type="button"
            className="px-6 py-2 rounded-lg bg-accent2 text-white hover:bg-accent1"
            onClick={() => navigate("/standalone")}
          >
            Try again
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="w-[100dvw] min-h-screen flex items-center justify-center bg-background1 px-4 py-12 focus:outline-none"
    >
      <Helmet><title>Signing In – Knowtex</title></Helmet>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-knowtex-shadow p-10 text-center">
        <div
          aria-hidden="true"
          className="w-10 h-10 mx-auto mb-5 border-4 border-accent2 border-t-transparent rounded-full animate-spin"
        />
        <h1 className="text-2xl text-accent1 font-semibold mb-3">
          Completing Sign-In
        </h1>
        <p role="status" className="text-knowtex-grey">
          We are validating your account and creating your session.
        </p>
      </div>
    </main>
  );
}
